import { type FC } from 'react'

import { Search } from './Search'
import type { LocalSearchProps } from './types'

/**
 * LocalSearch - Local search component for Select
 *
 * Local search component with automatic options filtering.
 * Renders a Search component with local search options.
 * searchBy and searchFilter props are used by useSelectSearch for filtering.
 *
 * @example
 * ```tsx
 * // Search by label (default)
 * <Select mode="single" value={value} onChange={setValue} options={options}>
 *   <Select.LocalSearch />
 * </Select>
 * ```
 *
 * @example
 * ```tsx
 * // Search by value
 * <Select mode="single" value={value} onChange={setValue} options={options}>
 *   <Select.LocalSearch searchBy="value" />
 * </Select>
 * ```
 *
 * @example
 * ```tsx
 * // Search on label and value
 * <Select mode="single" value={value} onChange={setValue} options={options}>
 *   <Select.LocalSearch searchBy="both" placeholder="Search..." />
 * </Select>
 * ```
 *
 * @example
 * ```tsx
 * // Search with custom filter
 * <Select mode="single" value={value} onChange={setValue} options={options}>
 *   <Select.LocalSearch
 *     searchFilter={(option, search) => option.value.startsWith(search)}
 *   />
 * </Select>
 * ```
 */
const LocalSearchComponent: FC<LocalSearchProps> = ({
  searchBy: _searchBy = 'both',
  placeholder,
}) => {
  // Renders a Search with local search options
  // searchBy and searchFilter props are extracted by useSelectSearch
  return <Search placeholder={placeholder} />
}

LocalSearchComponent.displayName = 'SelectLocalSearch'

export const LocalSearch = LocalSearchComponent
